import * as React from "react";
import { Eye, FolderOpen, Grid3x3, Home } from "lucide-react";
import { Link } from "react-router";

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import type { BusinessReport } from "@/modules/snapshots/businessReports.types";
import type { SnapshotCollection } from "@/modules/snapshots/snapshotCollection.types";
import type { SnapshotView } from "@/modules/snapshots/snapshotViews.types";

interface Props {
  mode: "collection" | "contract-family-projection" | "pipeline-preview" | "view";
  snapshotCollection: SnapshotCollection | null;
  businessReport: BusinessReport | null;
  snapshotView?: SnapshotView;
}

export const SnapshotGridBreadcrumbs: React.FC<Props> = ({
  mode,
  snapshotCollection,
  businessReport,
  snapshotView,
}) => {
  if (mode === "view") {
    if (!snapshotView) return null;
    return (
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/snapshots" className="flex items-center gap-1.5">
                <Home className="h-3.5 w-3.5" aria-hidden="true" />
                Snapshots
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/snapshots/views" className="flex items-center gap-1.5">
                <Eye className="h-3.5 w-3.5" aria-hidden="true" />
                Views
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{snapshotView.name}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
    );
  }

  if (!snapshotCollection || !businessReport) return null;
  const collectionLink = `/snapshots?businessReportId=${businessReport.id}&snapshotCollectionId=${snapshotCollection.id}`;

  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <Link to="/snapshots" className="flex items-center gap-1.5">
              <Home className="h-3.5 w-3.5" aria-hidden="true" />
              Snapshots
            </Link>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <Link to={collectionLink} className="flex items-center gap-1.5">
              <FolderOpen className="h-3.5 w-3.5" aria-hidden="true" />
              {snapshotCollection.name}
            </Link>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem>
          <BreadcrumbPage className="flex items-center gap-1.5">
            <Grid3x3 className="h-3.5 w-3.5" aria-hidden="true" />
            View Snapshots
          </BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );
};
